import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { NotebookIcon as Notebook, PlusIcon as Plus, FloppyDiskIcon as FloppyDisk, EyeIcon as Eye, CheckCircleIcon as CheckCircle, TrashIcon as Trash, PencilIcon as Pencil, ArrowLeftIcon as ArrowLeft, WarningIcon as Warning, TwitterLogoIcon as TwitterLogo, FacebookLogoIcon as FacebookLogo, LinkedinLogoIcon as LinkedinLogo, EnvelopeSimpleIcon as EnvelopeSimple, LinkIcon as LinkIcon, PaperPlaneTiltIcon as PaperPlaneTilt } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Admin journal CMS. Lets the operator write Markdown-bodied posts,
 * save drafts, preview live, and publish/archive — no code deploy.
 *
 * The static JSX posts in /content/journal/ keep working alongside
 * these; Journal.jsx index merges both sources so the existing
 * curated editorials stay where they are.
 *
 * Editor state machine:
 *   list   → table of all posts (drafts + published + archived)
 *   create → blank editor
 *   edit   → editor preloaded with the chosen post
 */
const SECTIONS = [
  "Editorial", "For breeders", "For buyers", "Trust & safety",
  "Pedigrees", "Platform", "Behind the scenes",
];

const blankForm = {
  id: null,
  title: "",
  dek: "",
  section: "Editorial",
  body_md: "",
  cover_image_url: "",
  slug: "",
  disclaimer: "",
  status: "draft",
};

// PRD rule: no mention of the trademarked AKC initialism anywhere on
// the platform. The editor surfaces a soft warning and blocks publish
// if the body, title, or dek contains "AKC" as a whole word — typos
// like "BACK" / "TRACK" are left alone.
const AKC_RE = /\bAKC\b/i;

function detectAkcViolation(form) {
  const fields = {
    title: form.title || "",
    dek: form.dek || "",
    body_md: form.body_md || "",
  };
  const offenders = Object.entries(fields).filter(([, v]) => AKC_RE.test(v));
  return offenders.map(([k]) => k);
}

export default function AdminJournal() {
  const [posts, setPosts] = useState(null);
  const [view, setView] = useState("list"); // list | edit
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const [sharePost, setSharePost] = useState(null); // post to surface in the post-publish share dialog

  const load = () => {
    api.get("/admin/journal/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch((e) => {
        toast.error(formatApiError(e));
        setPosts([]);
      });
  };
  useEffect(load, []);

  const startCreate = () => { setForm(blankForm); setView("edit"); };
  const startEdit = (p) => { setForm({ ...blankForm, ...p }); setView("edit"); };
  const cancel = () => { setForm(blankForm); setView("list"); };

  const save = async (publishNow = false) => {
    // PRD rule guard — blocks publish if AKC trademark appears in
    // title/dek/body. Drafts are allowed through so authors can edit
    // their way out of it without losing work.
    if (publishNow) {
      const offenders = detectAkcViolation(form);
      if (offenders.length) {
        toast.error(
          `Cannot publish — "AKC" appears in ${offenders.join(", ")}. ` +
          "Platform policy: never reference the AKC trademark.",
        );
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        dek: form.dek.trim(),
        section: form.section,
        body_md: form.body_md,
        cover_image_url: form.cover_image_url || null,
        slug: form.slug || undefined,
        disclaimer: form.disclaimer || "",
      };
      if (form.id) {
        if (publishNow) payload.status = "published";
        const { data } = await api.patch(`/admin/journal/posts/${form.id}`, payload);
        toast.success(publishNow ? "Published." : "Saved.");
        setForm({ ...blankForm, ...data });
        if (publishNow && data?.status === "published") setSharePost(data);
      } else {
        const { data } = await api.post("/admin/journal/posts", payload);
        if (publishNow) {
          const { data: pub } = await api.patch(`/admin/journal/posts/${data.id}`, { status: "published" });
          toast.success("Created & published.");
          setForm({ ...blankForm, ...pub });
          if (pub?.status === "published") setSharePost(pub);
        } else {
          toast.success("Saved as draft.");
          setForm({ ...blankForm, ...data });
        }
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this post? It will be hidden from the public journal.")) return;
    try {
      await api.delete(`/admin/journal/posts/${id}`);
      toast.success("Archived.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!posts) return <AppShell><div className="max-w-5xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-journal">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
                <Notebook size={26} weight="duotone" /> Journal
              </h1>
              <p className="text-[13px] text-[#6B6B6B] mt-1 max-w-2xl">
                Write and publish editorial posts. Markdown body, optional cover
                image. Drafts stay private; publishing pushes the post to{" "}
                <Link to="/journal" className="underline">/journal</Link> immediately.
              </p>
            </div>
            {view === "list" ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/journal/settings"
                  className="inline-flex items-center gap-1 text-[12px] text-[#6B6B6B] hover:text-ink underline underline-offset-2"
                  data-testid="admin-journal-settings-link"
                >
                  Footer disclaimer settings
                </Link>
                <Button onClick={startCreate} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="admin-journal-new">
                  <Plus size={14} weight="bold" className="mr-1" /> New post
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={cancel} className="rounded-full">
                <ArrowLeft size={14} weight="bold" className="mr-1" /> Back to list
              </Button>
            )}
          </div>
        </header>

        {view === "list" ? (
          <PostList posts={posts} onEdit={startEdit} onArchive={archive} />
        ) : (
          <Editor form={form} setForm={setForm} busy={busy} onSave={save} />
        )}

        <PostPublishShareDialog post={sharePost} onClose={() => setSharePost(null)} />
      </div>
    </AppShell>
  );
}

function PostList({ posts, onEdit, onArchive }) {
  if (posts.length === 0) {
    return (
      <section className="rounded-2xl border-2 border-dashed border-[#EAEAEA] p-10 text-center" data-testid="admin-journal-empty">
        <Notebook size={28} weight="duotone" className="text-[#9A9A9A] mx-auto mb-2" />
        <p className="text-[13px] text-[#6B6B6B]">
          No posts yet. Click <strong>New post</strong> to write your first.
        </p>
      </section>
    );
  }
  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white overflow-hidden" data-testid="admin-journal-list">
      <table className="w-full text-[13px]">
        <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-wider text-[#6B6B6B]">
          <tr>
            <th className="text-left px-3 py-2">Title</th>
            <th className="text-left px-3 py-2">Section</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Updated</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => (
            <tr key={p.id} className="border-t border-[#F1ECDD]" data-testid={`admin-journal-row-${p.slug}`}>
              <td className="px-3 py-2.5">
                <div className="font-bold text-ink">{p.title}</div>
                <div className="text-[11px] text-[#6B6B6B]">/journal/{p.slug}</div>
              </td>
              <td className="px-3 py-2.5">
                <span className="text-[11px] uppercase tracking-wider text-gold-dark">{p.section}</span>
              </td>
              <td className="px-3 py-2.5">
                <StatusPill status={p.status} />
              </td>
              <td className="px-3 py-2.5 text-[#6B6B6B]">
                {new Date(p.updated_at).toLocaleDateString()}
              </td>
              <td className="px-3 py-2.5 text-right space-x-1">
                <Button size="sm" variant="outline" onClick={() => onEdit(p)} className="rounded-full h-7 text-[11px]" data-testid={`admin-journal-edit-${p.slug}`}>
                  <Pencil size={11} weight="bold" className="mr-1" /> Edit
                </Button>
                {p.status !== "archived" && (
                  <Button size="sm" variant="outline" onClick={() => onArchive(p.id)} className="rounded-full h-7 text-[11px] text-crimson border-crimson/40 hover:bg-crimson hover:text-white">
                    <Trash size={11} weight="bold" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:     { label: "Draft",     classes: "bg-[#FBF7EC] text-[#8a6a00] border-[#E5D08A]" },
    published: { label: "Published", classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    archived:  { label: "Archived",  classes: "bg-[#F4F4F4] text-[#6B6B6B] border-[#E5E5E5]" },
  };
  const it = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center text-[10.5px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 ${it.classes}`}>
      {it.label}
    </span>
  );
}

function Editor({ form, setForm, busy, onSave }) {
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const html = React.useMemo(() => {
    try {
      return DOMPurify.sanitize(marked.parse(form.body_md || ""));
    } catch {
      return "";
    }
  }, [form.body_md]);
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-journal-editor">
      <section className="space-y-4">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="journal-title" />
        </div>
        <div>
          <Label className="text-xs">Dek (sub-headline)</Label>
          <Input value={form.dek} onChange={(e) => upd("dek", e.target.value)} data-testid="journal-dek" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Section</Label>
            <select
              value={form.section}
              onChange={(e) => upd("section", e.target.value)}
              className="w-full mt-1 rounded-md border border-input bg-white px-3 py-2 text-sm"
              data-testid="journal-section"
            >
              {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Slug <span className="text-[#9A9A9A]">(auto from title)</span></Label>
            <Input
              value={form.slug}
              onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              placeholder="auto-generated"
              data-testid="journal-slug"
              className="font-tabular"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Cover image URL (optional)</Label>
          <Input
            value={form.cover_image_url || ""}
            onChange={(e) => upd("cover_image_url", e.target.value)}
            placeholder="https://…"
            data-testid="journal-cover"
            className="font-tabular text-[12px]"
          />
        </div>
        <div>
          <Label className="text-xs">
            Per-post disclaimer{" "}
            <span className="text-[#9A9A9A]">(optional — renders above the body in italic)</span>
          </Label>
          <Textarea
            value={form.disclaimer || ""}
            onChange={(e) => upd("disclaimer", e.target.value)}
            rows={2}
            placeholder="e.g., Published with permission of the kennel featured. Health & DNA claims are paraphrased from documents the kennel provided."
            data-testid="journal-disclaimer"
            className="text-[12.5px]"
          />
          <p className="text-[10.5px] text-[#9A9A9A] mt-1">
            Use this for one-off notices (consent, source attribution,
            mid-post caveats). The standing site-wide footer disclaimer
            renders separately on every post — edit it on the{" "}
            <Link to="/admin/journal/settings" className="underline">settings page</Link>.
          </p>
        </div>
        <div>
          <Label className="text-xs">Body (Markdown)</Label>
          <Textarea
            value={form.body_md}
            onChange={(e) => upd("body_md", e.target.value)}
            rows={18}
            className="font-mono text-[12.5px]"
            placeholder={`## A heading\n\nA paragraph with **bold** and a [link](https://armstrongk9registry.com).\n\n- A bullet\n- Another bullet\n\n> A blockquote sets a quieter tone.`}
            data-testid="journal-body"
          />
          <p className="text-[10.5px] text-[#9A9A9A] mt-1">
            Markdown: # headings, **bold**, *italic*, [links](url), &gt; quotes,
            - lists. Live preview on the right.
          </p>
        </div>
        {(() => {
          const offenders = detectAkcViolation(form);
          return offenders.length ? (
            <div
              className="rounded-md border border-[#C1272D]/40 bg-[#C1272D]/5 px-3 py-2 text-[12px] text-[#C1272D] flex items-start gap-2"
              data-testid="journal-akc-warning"
            >
              <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
              <span>
                <strong>"AKC" detected</strong> in {offenders.join(", ")}. Platform
                policy prohibits referencing the AKC trademark — publish is blocked
                until the term is removed. Drafts still save.
              </span>
            </div>
          ) : null;
        })()}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={() => onSave(false)} disabled={busy} variant="outline" className="rounded-full" data-testid="journal-save-draft">
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {busy ? "Saving…" : "Save draft"}
          </Button>
          {(() => {
            const akcBlocked = detectAkcViolation(form).length > 0;
            return (
              <Button
                onClick={() => onSave(true)}
                disabled={busy || akcBlocked}
                className="rounded-full bg-ink hover:bg-crimson text-white"
                data-testid="journal-publish"
                title={akcBlocked ? "Resolve the AKC trademark warning above to enable publish." : ""}
              >
                <CheckCircle size={14} weight="bold" className="mr-1" />
                {busy ? "Publishing…" : (form.status === "published" ? "Update published" : "Publish now")}
              </Button>
            );
          })()}
        </div>
      </section>
      <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6" data-testid="journal-preview">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#6B6B6B] flex items-center gap-1 mb-3">
          <Eye size={11} weight="duotone" /> Live preview
        </p>
        {form.cover_image_url && (
          <img
            src={form.cover_image_url}
            alt=""
            className="rounded-xl w-full h-44 object-cover mb-4 border border-[#EAEAEA]"
          />
        )}
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">{form.section}</p>
        <h1 className="font-spectral font-light text-3xl tracking-tight leading-[1.1]">
          {form.title || "Your title here"}
        </h1>
        <p className="mt-3 text-[14px] text-[#3a3a3a] leading-relaxed">{form.dek || "A short dek shows up under the title."}</p>
        <div className="gold-hairline mt-5 mb-5 max-w-md" />
        <div
          className="journal-prose prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </section>
    </div>
  );
}

/**
 * Post-publish share dialog — surfaces immediately after a post goes
 * live. Uses intent URLs (X, Facebook, LinkedIn, email) so no API
 * keys / OAuth tokens are required; the admin clicks each network
 * and confirms the share in the third-party's own composer. A
 * `data-rh` analytics fire on click tells us which channels actually
 * get used so we can later prune dead targets.
 *
 * Why open-intent instead of API-driven: brand voice. The founder
 * wants every social post to be edited live, not boilerplated by
 * the platform. The intent URLs pre-fill but let her tweak.
 */
function PostPublishShareDialog({ post, onClose }) {
  const [copied, setCopied] = React.useState(false);
  if (!post) return null;
  const origin = typeof window !== "undefined" ? webOrigin() : "";
  const url = `${origin}/journal/${post.slug}`;
  const title = post.title || "";
  const dek = post.dek || "";
  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title);
  const encDek = encodeURIComponent(dek);

  // Pre-composed tweet — title + URL fits under 280 chars even for
  // long titles. Dek omitted because Twitter strips URL preview when
  // OG meta is present, so the dek would just clutter the tweet.
  const targets = [
    {
      label: "Share on X",
      icon: TwitterLogo,
      href: `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`,
      colorClass: "bg-[#000] text-white hover:bg-[#1a1a1a]",
      testId: "post-publish-share-x",
    },
    {
      label: "Share on Facebook",
      icon: FacebookLogo,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      colorClass: "bg-[#1877F2] text-white hover:bg-[#0e60c6]",
      testId: "post-publish-share-facebook",
    },
    {
      label: "Share on LinkedIn",
      icon: LinkedinLogo,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      colorClass: "bg-[#0A66C2] text-white hover:bg-[#084c92]",
      testId: "post-publish-share-linkedin",
    },
    {
      label: "Email a friend",
      icon: EnvelopeSimple,
      href: `mailto:?subject=${encTitle}&body=${encDek ? encDek + "%0A%0A" : ""}${encUrl}`,
      colorClass: "bg-ink text-white hover:bg-crimson",
      testId: "post-publish-share-email",
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Select the link manually.");
    }
  };

  return (
    <Dialog open={!!post} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="post-publish-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-spectral">
            <PaperPlaneTilt size={20} weight="duotone" className="text-gold-dark" />
            Share it
          </DialogTitle>
          <DialogDescription className="text-[12.5px] leading-relaxed">
            Your post is live at <span className="font-mono text-[11.5px] text-ink">/journal/{post.slug}</span>.
            One click per network to amplify it — each opens the network's own composer so you can tweak the wording.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-[#EAEAEA] bg-[#FBF7EC]/60 p-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-1">Now live</p>
          <p className="font-spectral text-[16px] tracking-tight leading-tight text-ink line-clamp-2">{post.title}</p>
          {dek && <p className="text-[12px] text-[#6B6B6B] mt-1 line-clamp-2">{dek}</p>}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {targets.map((t) => (
            <a
              key={t.testId}
              href={t.href}
              target={t.href.startsWith("mailto:") ? undefined : "_blank"}
              rel={t.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
              data-testid={t.testId}
              className={`inline-flex items-center justify-center gap-2 h-10 rounded-full text-[12px] font-bold tracking-[0.04em] transition-colors ${t.colorClass}`}
            >
              <t.icon size={14} weight="fill" />
              {t.label}
            </a>
          ))}
        </div>

        <button
          type="button"
          onClick={copy}
          data-testid="post-publish-share-copy"
          className="inline-flex items-center justify-center gap-2 h-10 rounded-full border border-[#EAEAEA] bg-white text-[12px] font-bold tracking-[0.04em] text-ink hover:bg-[#FBF7EC] hover:border-[#C9A227]/60 transition-colors"
        >
          {copied ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : <LinkIcon size={14} weight="bold" />}
          {copied ? "Copied" : "Copy link"}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-[#6B6B6B] hover:text-ink underline underline-offset-2 mx-auto block"
          data-testid="post-publish-share-skip"
        >
          Skip for now
        </button>
      </DialogContent>
    </Dialog>
  );
}
